import React, { useEffect, useState, useRef, Suspense } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { initFeatherIcons, getPatientDashboard, logoutUser, api } from '../../utils/api';
import imageCompression from 'browser-image-compression';
import DoctorSearch from '../../components/DoctorSearch';
import ReportsSection from '../../components/ReportsSection';
import PrescriptionList from '../../components/prescription/PrescriptionList';
import HealthTips from '../../components/HealthTips';
import SubscriptionManagement from '../../components/subscription/SubscriptionManagement';
import './PatientDashboard.css';
import { paymentAPI } from '../../services/marketplaceAPI';
import '../../styles/marketplace.css';
import { ThemeProvider } from '@mui/material/styles';
import { Box, Button, Chip, Paper, Typography, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Avatar, IconButton, Divider, MenuItem, Select, FormControl, InputLabel, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import LockIcon from '@mui/icons-material/Lock';
import SaveIcon from '@mui/icons-material/Save';
import { websocketService } from '../../services/websocket';
import { useNotifications } from '../../contexts/NotificationContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../utils/translations';
import LanguageToggle from '../../components/LanguageToggle';
import muiTheme from '../../theme/muiTheme';
import { getLogoUrl } from '../../utils/logoUtils';
import { getImageUrl } from '../../utils/imageUtils';

// Lazy load AI Health Support Button
const AIHealthSupportButton = React.lazy(() => import('../../components/ai-health/AIHealthSupportButton'));
const AIChatDrawer = React.lazy(() => import('../../components/ai-health/AIChatDrawer'));

const PatientDashboard = () => {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const { notifications, unreadCount, markAsRead, markAllAsRead, requestPermission } = useNotifications();
  const { language } = useLanguage();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [logoUrl, setLogoUrl] = useState(getLogoUrl());
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDoctorSearch, setShowDoctorSearch] = useState(false);
  const [cancellingAppointment, setCancellingAppointment] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [chatAssignedAgent, setChatAssignedAgent] = useState(null);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    phone: '',
    age: '',
    birthdate: '',
    weight: '',
    height: '',
    address: '',
    bloodGroup: '',
    emergencyContact: { name: '', phone: '', relationship: '' },
    medicalHistory: [],
    allergies: [],
    currentMedications: []
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [aiDrawerOpen, setAiDrawerOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const [bannerSlideIndex, setBannerSlideIndex] = useState(0);
  const [bannerImageErrors, setBannerImageErrors] = useState({});
  const fileInputRef = useRef(null);
  const notificationRef = useRef(null);
  const bannerIntervalRef = useRef(null);

  // Request notification permission on mount
  useEffect(() => {
    requestPermission();
  }, [requestPermission]);

  // Force navy blue CSS variables - override any purple from other CSS files
  useEffect(() => {
    // #region agent log - Force navy blue CSS variables
    const forceNavyBlueVariables = () => {
      const root = document.documentElement;
      const navyBlueVars = {
        '--primary-color': '#1E40AF',
        '--primary-light': '#2563EB',
        '--primary-dark': '#1E3A8A',
        '--primary-gradient': 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
        '--primary-gradient-hover': 'linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)',
      };
      
      // Force set all navy blue variables on :root (inline style has highest specificity)
      Object.entries(navyBlueVars).forEach(([key, value]) => {
        root.style.setProperty(key, value);
      });
      
      // Also set on the patient dashboard container
      const dashboardContainer = document.querySelector('.patient-dashboard-new');
      if (dashboardContainer) {
        Object.entries(navyBlueVars).forEach(([key, value]) => {
          dashboardContainer.style.setProperty(key, value);
        });
      }
      
      // Log current values for debugging
      const currentPrimary = getComputedStyle(root).getPropertyValue('--primary-color').trim();
      const currentGradient = getComputedStyle(root).getPropertyValue('--primary-gradient').trim();
      const isPurple = currentPrimary.includes('6366F1') || currentPrimary.includes('6366f1') || 
                       currentGradient.includes('6366F1') || currentGradient.includes('6366f1');
      
    };
    
    // Set immediately
    forceNavyBlueVariables();
    
    // Set again after delays to override any late-loading CSS
    const timer1 = setTimeout(forceNavyBlueVariables, 100);
    const timer2 = setTimeout(forceNavyBlueVariables, 500);
    const timer3 = setTimeout(forceNavyBlueVariables, 1000);
    const timer4 = setTimeout(forceNavyBlueVariables, 2000);
    
    // Use MutationObserver to detect when CSS variables might be changed
    const observer = new MutationObserver(() => {
      forceNavyBlueVariables();
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style', 'class']
    });
    
    // Also check periodically to catch any overrides
    const interval = setInterval(forceNavyBlueVariables, 3000);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
      clearInterval(interval);
      observer.disconnect();
    };
    // #endregion agent log
  }, []);

  // Helper function to format timestamps
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    return time.toLocaleDateString();
  };

  // Toggle dark mode
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.documentElement.setAttribute('data-theme', !isDarkMode ? 'dark' : 'light');
  };

  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotificationDropdown(false);
      }
    };

    if (showNotificationDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotificationDropdown]);

  // Initialize theme
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setIsDarkMode(savedTheme === 'dark');
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  // Save theme preference
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Monitor theme changes and update logo
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
      setIsDarkMode(isDark);
      setLogoUrl(getLogoUrl());
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
    return () => observer.disconnect();
  }, []);

  // Banner slideshow auto-play - Initialize on mount and when activeTab changes
  useEffect(() => {
    // Reset banner index on mount to ensure it starts from the beginning
    setBannerSlideIndex(0);
    
    // Log banner image URLs for debugging
    const bannerUrls = [
      "https://res.cloudinary.com/dmmetrvda/image/upload/v1766359619/weheal/banners/banner-1_new.png",
      "https://res.cloudinary.com/dmmetrvda/image/upload/v1766359621/weheal/banners/banner-2_new.png",
      "https://res.cloudinary.com/dmmetrvda/image/upload/v1766359623/weheal/banners/banner-3_new.png"
    ];
    // #region agent log
    // #endregion
    
    // Clear any existing interval first
    if (bannerIntervalRef.current) {
      clearInterval(bannerIntervalRef.current);
      bannerIntervalRef.current = null;
    }
    
    // Only start slideshow if we're on the overview tab
    if (activeTab === 'overview') {
      // Small delay to ensure DOM is ready and images can load
      const initTimer = setTimeout(() => {
      bannerIntervalRef.current = setInterval(() => {
        setBannerSlideIndex((prevIndex) => {
          const newIndex = (prevIndex + 1) % 3;
          return newIndex;
        });
      }, 5000); // Change slide every 5 seconds
      }, 100);

      return () => {
        clearTimeout(initTimer);
        if (bannerIntervalRef.current) {
          clearInterval(bannerIntervalRef.current);
          bannerIntervalRef.current = null;
        }
      };
    }

    return () => {
      if (bannerIntervalRef.current) {
        clearInterval(bannerIntervalRef.current);
        bannerIntervalRef.current = null;
      }
    };
  }, [activeTab]);

  const handleLogout = async () => {
    try {
      await logoutUser();
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleCancelAppointment = async (appointmentId) => {
    if (!appointmentId) {
      alert('Invalid appointment');
      return;
    }

    if (!window.confirm(t('dashboard.areYouSureCancel', language))) {
      return;
    }

    try {
      setCancellingAppointment(true);
      const response = await api.post(`/patient/cancel-appointment/${appointmentId}`);
      
      if (response.data.success) {
        setDashboardData(prev => ({
          ...prev,
          patientData: {
            ...prev.patientData,
            upcomingAppointments: prev.patientData.upcomingAppointments.filter(
              apt => apt._id !== appointmentId
            )
          }
        }));
        showNotification(t('dashboard.appointmentCancelled', language), 'success');
      }
    } catch (err) {
      console.error('Error cancelling appointment:', err);
      const errorMessage = err.response?.data?.message || err.message || t('dashboard.errorCancelling', language);
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    } finally {
      setCancellingAppointment(false);
    }
  };

  const handleStartVideoCall = (appointmentId) => {
    navigate(`/video-call/${appointmentId}`);
  };

  const handleEmergencyService = () => {
    navigate('/emergency');
  };

  const handleNurseService = () => {
    navigate('/emergency/nurse-marketplace');
  };

  const showNotification = (message, type = 'info') => {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    notification.textContent = message;
    document.body.appendChild(notification);
    
    // Remove after 3 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 3000);
  };

  const fetchProfileData = async () => {
    try {
      const response = await api.get('/patient-profile/profile');
      if (response.data.success) {
        const userData = response.data.data;
        setProfileData({
          name: userData.name || '',
          phone: userData.phone || '',
          age: userData.patientDetails?.age || '',
          birthdate: userData.patientDetails?.DOB 
            ? new Date(userData.patientDetails.DOB).toISOString().split('T')[0]
            : '',
          weight: userData.patientDetails?.weight || '',
          height: userData.patientDetails?.height || '',
          address: userData.patientDetails?.address || '',
          bloodGroup: userData.patientDetails?.bloodGroup || '',
          emergencyContact: userData.patientDetails?.emergencyContact || { name: '', phone: '', relationship: '' },
          medicalHistory: userData.patientDetails?.medicalHistory || [],
          allergies: userData.patientDetails?.allergies || [],
          currentMedications: userData.patientDetails?.currentMedications || []
        });
        if (userData.profilePicture) {
          setProfilePicture(userData.profilePicture);
        }
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  };

  // Lightweight patient-side chat widget state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatImageUpload, setChatImageUpload] = useState(null);
  const [chatImagePreview, setChatImagePreview] = useState(null);
  useEffect(() => {
    const handlerAssigned = (data) => setChatAssignedAgent(data.agent);
    const handlerMessage = (msg) => {
    if (msg.type === 'image') {
      setChatMessages(prev => [...prev, { 
        from: 'agent', 
        type: 'image',
        imageUrl: msg.imageUrl,
        fileName: msg.fileName,
        at: msg.at 
      }]);
    } else {
      setChatMessages(prev => [...prev, msg]);
    }
  };
    const handlerChatEnded = (data) => {
      // Chat was ended by the agent
      setChatOpen(false);
      setChatMessages([]);
      setChatInput('');
      setChatAssignedAgent(null);
      // You could show a notification here if needed
    };
    
    try {
      websocketService.connect();
      websocketService.subscribe('chat:assigned', handlerAssigned);
      websocketService.subscribe('chat:message', handlerMessage);
      websocketService.subscribe('chat:ended', handlerChatEnded);
    } catch {}
    
    return () => {
      websocketService.unsubscribe('chat:assigned', handlerAssigned);
      websocketService.unsubscribe('chat:message', handlerMessage);
      websocketService.unsubscribe('chat:ended', handlerChatEnded);
    };
  }, []);

  const startChat = () => {
    setChatOpen(true);
    try {
      const userId = user?._id || user?.id;
      if (!userId) {
        console.error('Cannot start chat: No user ID found');
        return;
      }
      console.log('Patient: Sending chat:new request with userId:', userId);
      websocketService.send('chat:new', { patientId: userId, timestamp: new Date().toISOString() });
      console.log('Patient: chat:new request sent successfully');
    } catch (error) {
      console.error('Error starting chat:', error);
    }
  };

  const sendChat = () => {
    if (!chatInput.trim()) return;
    const msg = {
      from: 'patient',
      text: chatInput.trim(),
      at: new Date().toISOString()
    };
    setChatMessages(prev => [...prev, msg]);
    setChatInput('');
    try {
      if (chatAssignedAgent?.id) {
        websocketService.send('chat:message', {
          toUserId: chatAssignedAgent.id,
          message: { ...msg, patientId: user?._id || user?.id }
        });
      }
    } catch {}
  };

  const endChat = () => {
    try {
      if (chatAssignedAgent?.id) {
        websocketService.send('chat:end', { 
          patientId: user?._id || user?.id, 
          agentId: chatAssignedAgent.id 
        });
      }
    } catch {}
    
    // Clear chat state
    setChatOpen(false);
    setChatMessages([]);
    setChatInput('');
    setChatAssignedAgent(null);
    setChatImageUpload(null);
    setChatImagePreview(null);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }
      
      setChatImageUpload(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setChatImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const sendImage = () => {
    if (!chatImageUpload) return;
    
    const msg = {
      from: 'patient',
      type: 'image',
      imageUrl: chatImagePreview,
      fileName: chatImageUpload.name,
      at: new Date().toISOString()
    };
    
    setChatMessages(prev => [...prev, msg]);
    
    try {
      if (chatAssignedAgent?.id) {
        websocketService.send('chat:message', {
          toUserId: chatAssignedAgent.id,
          message: { ...msg, patientId: user?._id || user?.id }
        });
      }
    } catch {}
    
    // Clear image state
    setChatImageUpload(null);
    setChatImagePreview(null);
  };

  const removeImage = () => {
    setChatImageUpload(null);
    setChatImagePreview(null);
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      // Prepare data with DOB field for backend
      const dataToSend = {
        ...profileData,
        DOB: profileData.birthdate || undefined
      };
      const response = await api.put('/patient-profile/profile', dataToSend);
      if (response.data.success) {
        showNotification('Profile updated successfully', 'success');
        setShowProfileEdit(false);
        // Refresh dashboard data
        const dashResponse = await getPatientDashboard();
        if (dashResponse.success) {
          setDashboardData(dashResponse.data);
        }
        // Refresh profile data
        await fetchProfileData();
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      showNotification(err.response?.data?.message || 'Error updating profile', 'error');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showNotification('New passwords do not match', 'error');
      return;
    }
    
    try {
      const response = await api.put('/patient-profile/change-password', passwordData);
      if (response.data.success) {
        showNotification('Password changed successfully', 'success');
        setShowPasswordChange(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (err) {
      console.error('Error changing password:', err);
      showNotification(err.response?.data?.message || 'Error changing password', 'error');
    }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'PatientDashboard.js:handleProfilePictureUpload-start',message:'Upload started',data:{fileName:file.name,fileSize:file.size,fileSizeMB:(file.size / (1024 * 1024)).toFixed(2),fileType:file.type},timestamp:Date.now(),sessionId:'debug-session',runId:'upload-debug',hypothesisId:'H4'})}).catch(()=>{});
    // #endregion

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showNotification('Please select an image file', 'error');
      return;
    }

    // Validate file size (max 10MB before compression)
    if (file.size > 10 * 1024 * 1024) {
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'PatientDashboard.js:handleProfilePictureUpload-size-check-failed',message:'File size validation failed on frontend',data:{fileSize:file.size,fileSizeMB:(file.size / (1024 * 1024)).toFixed(2),maxSize:10 * 1024 * 1024},timestamp:Date.now(),sessionId:'debug-session',runId:'upload-debug',hypothesisId:'H4'})}).catch(()=>{});
      // #endregion
      showNotification('File size must be less than 10MB', 'error');
      return;
    }
    
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'PatientDashboard.js:handleProfilePictureUpload-size-check-passed',message:'File size validation passed on frontend, starting compression',data:{fileSize:file.size,fileSizeMB:(file.size / (1024 * 1024)).toFixed(2)},timestamp:Date.now(),sessionId:'debug-session',runId:'upload-debug',hypothesisId:'H4'})}).catch(()=>{});
    // #endregion

    try {
      // Compress image before upload
      showNotification('Compressing image...', 'info');
      
      const options = {
        maxSizeMB: 1, // Maximum file size in MB (target: 1MB after compression)
        maxWidthOrHeight: 1000, // Maximum width or height
        useWebWorker: true, // Use web worker for better performance
        fileType: file.type // Preserve original file type
      };

      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'PatientDashboard.js:handleProfilePictureUpload-compression-start',message:'Starting image compression',data:{originalSize:file.size,originalSizeMB:(file.size / (1024 * 1024)).toFixed(2),compressionOptions:options},timestamp:Date.now(),sessionId:'debug-session',runId:'upload-debug',hypothesisId:'H4'})}).catch(()=>{});
      // #endregion

      const compressedFile = await imageCompression(file, options);
      
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'PatientDashboard.js:handleProfilePictureUpload-compression-complete',message:'Image compression completed',data:{originalSize:file.size,originalSizeMB:(file.size / (1024 * 1024)).toFixed(2),compressedSize:compressedFile.size,compressedSizeMB:(compressedFile.size / (1024 * 1024)).toFixed(2),compressionRatio:((1 - compressedFile.size / file.size) * 100).toFixed(1) + '%'},timestamp:Date.now(),sessionId:'debug-session',runId:'upload-debug',hypothesisId:'H4'})}).catch(()=>{});
      // #endregion

      const formData = new FormData();
      formData.append('profilePicture', compressedFile, compressedFile.name);

      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'PatientDashboard.js:handleProfilePictureUpload-upload-start',message:'Starting upload to server',data:{compressedSize:compressedFile.size,compressedSizeMB:(compressedFile.size / (1024 * 1024)).toFixed(2)},timestamp:Date.now(),sessionId:'debug-session',runId:'upload-debug',hypothesisId:'H4'})}).catch(()=>{});
      // #endregion

      console.log('Uploading profile picture...');
      const response = await api.post('/patient-profile/upload-profile-picture', formData, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        },
        timeout: 30000 // 30 seconds timeout for file upload
      });
      
      console.log('Upload response:', response.data);
      
      if (response.data.success && response.data.profilePicture) {
        // Update the profile picture in state
        setProfilePicture(response.data.profilePicture);
        
        showNotification('Profile picture updated successfully', 'success');
        
        // Refresh user data without full page reload
        setTimeout(async () => {
          try {
            const userResponse = await api.get('/patient-profile');
            if (userResponse.data.success && userResponse.data.data) {
              setProfilePicture(userResponse.data.data.profilePicture);
            }
          } catch (refreshErr) {
            console.error('Error refreshing user data:', refreshErr);
          }
        }, 500);
      } else {
        showNotification(response.data.message || 'Failed to upload profile picture', 'error');
      }
    } catch (err) {
      console.error('Error uploading profile picture:', err);
      console.error('Error details:', err.response?.data);
      
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'PatientDashboard.js:handleProfilePictureUpload-error',message:'Upload error caught',data:{errorMessage:err.message,status:err.response?.status,responseData:err.response?.data,originalFileSize:file?.size,originalFileSizeMB:file?.size ? (file.size / (1024 * 1024)).toFixed(2) : null,isCompressionError:!err.response},timestamp:Date.now(),sessionId:'debug-session',runId:'upload-debug',hypothesisId:'H4'})}).catch(()=>{});
      // #endregion
      
      let errorMessage = 'Error uploading profile picture';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // If compression failed, try uploading original file
      if (!err.response && file) {
        console.log('Compression failed, trying to upload original file...');
        try {
          const formData = new FormData();
          formData.append('profilePicture', file);
          
          const response = await api.post('/patient-profile/upload-profile-picture', formData, {
            headers: { 
              'Content-Type': 'multipart/form-data'
            },
            timeout: 30000
          });
          
          if (response.data.success && response.data.profilePicture) {
            setProfilePicture(response.data.profilePicture);
            showNotification('Profile picture updated successfully', 'success');
            setTimeout(async () => {
              try {
                const userResponse = await api.get('/patient-profile');
                if (userResponse.data.success && userResponse.data.data) {
                  setProfilePicture(userResponse.data.data.profilePicture);
                }
              } catch (refreshErr) {
                console.error('Error refreshing user data:', refreshErr);
              }
            }, 500);
            return;
          }
        } catch (fallbackErr) {
          console.error('Fallback upload also failed:', fallbackErr);
        }
      }
      
      showNotification(errorMessage, 'error');
    }
  };

  // Extract fetchDashboardData so it can be called on demand
  const fetchDashboardData = async () => {
    // #region agent log
    // #endregion agent log
    try {
      setLoading(true);
      const response = await getPatientDashboard();
      // #region agent log
      // #endregion agent log
      if (response.success) {
        setDashboardData(response.data);
        // #region agent log
        // #endregion agent log
        
        // Also fetch recent payment count for overview
        fetchRecentPaymentCount();
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data');
      // #region agent log
      // #endregion agent log
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchDashboardData();
      fetchProfileData();
    }
  }, [user]);

  // Refresh dashboard data when tab becomes visible (user returns to page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        fetchDashboardData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  // Scroll to top when tab changes
  useEffect(() => {
    // #region agent log
    const scrollBefore = window.scrollY || document.documentElement.scrollTop;
    const mainElement = document.querySelector('.dashboard-main');
    const contentArea = document.querySelector('.content-area');
    // #endregion agent log

    // Scroll window to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Also scroll main content area if it exists
    if (mainElement) {
      mainElement.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    // Also scroll content area if it exists
    if (contentArea) {
      contentArea.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // #region agent log
    setTimeout(() => {
      const scrollAfter = window.scrollY || document.documentElement.scrollTop;
    }, 100);
    // #endregion agent log
  }, [activeTab]);

  const fetchRecentPaymentCount = async () => {
    try {
      const response = await api.get('/payments/all-history');
      if (response.data.success) {
        const payments = response.data.data || [];
        
        // Count payments from this month
        const thisMonth = new Date().getMonth();
        const thisYear = new Date().getFullYear();
        const recentPayments = payments.filter(payment => {
          const paymentDate = new Date(payment.createdAt || payment.date);
          return paymentDate.getMonth() === thisMonth && paymentDate.getFullYear() === thisYear;
        });
        
        // Update the overview card
        const countElement = document.getElementById('recent-payments-count');
        if (countElement) {
          countElement.textContent = recentPayments.length;
        }
      }
    } catch (error) {
      console.error('Error fetching recent payment count:', error);
    }
  };

  // Debug: Check for purple colors in the DOM
  useEffect(() => {
    // #region agent log - Color diagnosis
    setTimeout(() => {
      const purpleColors = ['#9333ea', '#a855f7', '#8b5cf6', '#7c3aed', '#6366f1', 'rgb(147, 51, 234)', 'rgb(168, 85, 247)', 'rgb(139, 92, 246)', 'rgb(124, 58, 237)', 'rgb(99, 102, 241)'];
      const navyBlue = '#1E40AF';
      
      // Check CSS variables
      const root = document.documentElement;
      const primaryColor = getComputedStyle(root).getPropertyValue('--primary-color').trim();
      const primaryGradient = getComputedStyle(root).getPropertyValue('--primary-gradient').trim();
      
      // Check Material-UI buttons
      const muiButtons = document.querySelectorAll('.MuiButton-containedPrimary, .MuiButton-contained');
      const buttonColors = Array.from(muiButtons).slice(0, 5).map(btn => ({
        className: btn.className,
        backgroundColor: getComputedStyle(btn).backgroundColor,
        color: getComputedStyle(btn).color,
        hasPurple: purpleColors.some(pc => getComputedStyle(btn).backgroundColor.includes(pc) || getComputedStyle(btn).color.includes(pc))
      }));
      
      // Check all elements with purple colors
      const allElements = document.querySelectorAll('*');
      const purpleElements = [];
      allElements.forEach((el, idx) => {
        if (idx > 1000) return; // Limit search
        const bg = getComputedStyle(el).backgroundColor;
        const color = getComputedStyle(el).color;
        const borderColor = getComputedStyle(el).borderColor;
        const hasPurple = purpleColors.some(pc => 
          bg.includes(pc) || color.includes(pc) || borderColor.includes(pc)
        );
        if (hasPurple && purpleElements.length < 10) {
          purpleElements.push({
            tag: el.tagName,
            className: el.className,
            id: el.id,
            backgroundColor: bg,
            color: color,
            borderColor: borderColor
          });
        }
      });
      
    }, 2000);
    // #endregion agent log
    
    // #region agent log
    // #endregion agent log
    
    if (!showDoctorSearch) {
      // Only initialize icons when not showing DoctorSearch
      initFeatherIcons();
      
      // Re-initialize after a short delay to ensure DOM is ready
      const timer1 = setTimeout(() => {
        // #region agent log
        // #endregion agent log
        initFeatherIcons();
      }, 100);
      
      // Also re-initialize after a longer delay to catch any async rendering
      const timer2 = setTimeout(() => {
        initFeatherIcons();
      }, 300);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [dashboardData, activeTab, sidebarCollapsed, mobileMenuOpen, showDoctorSearch]);

  // Separate effect to re-initialize icons when coming back from DoctorSearch
  useEffect(() => {
    if (!showDoctorSearch) {
      // #region agent log
      // #endregion agent log
      
      // Re-initialize icons when coming back from DoctorSearch with multiple attempts
      const timer1 = setTimeout(() => {
        initFeatherIcons();
        // #region agent log
        // #endregion agent log
      }, 150);
      
      const timer2 = setTimeout(() => {
        initFeatherIcons();
        // #region agent log
        // #endregion agent log
      }, 400);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [showDoctorSearch]);

  // Watch for icon removal and re-initialize
  useEffect(() => {
    if (!showDoctorSearch && window.feather) {
      const observer = new MutationObserver((mutations) => {
        let shouldReinit = false;
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            // Check if any removed nodes were SVG icons
            mutation.removedNodes.forEach((node) => {
              if (node.nodeType === 1 && (node.tagName === 'svg' || node.querySelector('svg'))) {
                shouldReinit = true;
              }
            });
            // Check if any added nodes have data-feather attributes
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === 1 && (node.hasAttribute && node.hasAttribute('data-feather'))) {
                shouldReinit = true;
              }
            });
          }
        });
        
        if (shouldReinit) {
          // #region agent log
          // #endregion agent log
          setTimeout(() => {
            initFeatherIcons();
            // #region agent log
            // #endregion agent log
          }, 50);
        }
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
      
      return () => observer.disconnect();
    }
  }, [showDoctorSearch]);

  if (!user) {
    return <div className="loading-container">Please log in to view your dashboard.</div>;
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <h2>Error</h2>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  if (!dashboardData || !dashboardData.user) {
    return <div className="error-message">No dashboard data available</div>;
  }

  return (
    <ThemeProvider theme={muiTheme}>
    <div className={`patient-dashboard-new ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      {showDoctorSearch ? (
        <DoctorSearch 
          onBack={() => {
            // #region agent log
            // #endregion agent log
            setShowDoctorSearch(false);
          }} 
          onAppointmentBooked={async () => {
            // #region agent log
            // #endregion agent log
            // Refresh dashboard data to show the new appointment
            await fetchDashboardData();
            // #region agent log
            // #endregion agent log
            setShowDoctorSearch(false);
          }}
        />
      ) : (
        <div className="dashboard-layout">
          {/* Mobile Menu Toggle Button */}
          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <i data-feather={mobileMenuOpen ? "x" : "menu"}></i>
          </button>

          {/* Mobile Overlay */}
          {mobileMenuOpen && (
            <div 
              className="mobile-overlay" 
              onClick={() => setMobileMenuOpen(false)}
            />
          )}

          {/* Modern Sidebar Navigation */}
          <aside className={`sidebar-new ${sidebarCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            {/* Sidebar Header */}
            <div className="sidebar-header-new">
              <div className="sidebar-logo-new">
                <div className="logo-container-new">
                  <img 
                    src={logoUrl} 
                    alt="WeHeal" 
                    className="logo-image-new"
                    style={{ height: sidebarCollapsed ? '40px' : '48px', width: 'auto', objectFit: 'contain' }}
                    onError={(e) => {
                      // Fallback to icon if image fails
                      e.target.style.display = 'none';
                      const parent = e.target.parentElement;
                      if (parent && !parent.querySelector('.fallback-logo-icon')) {
                        const fallback = document.createElement('div');
                        fallback.className = 'fallback-logo-icon logo-icon-new';
                        fallback.innerHTML = '<i data-feather="heart"></i>';
                        parent.insertBefore(fallback, e.target);
                        if (window.feather) window.feather.replace();
                      }
                    }}
                  />
                  {!sidebarCollapsed && (
                    <div className="logo-text-container-new">
                      <span className="logo-text-new">WeHeal</span>
                      <span className="logo-tagline-new">Healthcare Platform</span>
                    </div>
                  )}
                </div>
              </div>
              <button 
                className="sidebar-collapse-btn-new"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <i data-feather={sidebarCollapsed ? "chevron-right" : "chevron-left"}></i>
              </button>
            </div>

            {/* User Profile Card */}
            {!sidebarCollapsed && (
              <div className="sidebar-profile-card-new" onClick={() => setShowProfileEdit(true)}>
                <div className="profile-avatar-new">
                  {profilePicture ? (
                    <img src={profilePicture} alt={user?.name} />
                  ) : (
                    <div className="profile-avatar-placeholder-new">
                      <i data-feather="user"></i>
                    </div>
                  )}
                  <div className="profile-status-new"></div>
                </div>
                <div className="profile-info-new">
                  <div className="profile-name-new">{user?.name || 'User'}</div>
                  <div className="profile-email-new">{user?.email || ''}</div>
                </div>
                <button className="profile-edit-btn-new" onClick={(e) => { e.stopPropagation(); setShowProfileEdit(true); }}>
                  <i data-feather="edit-3"></i>
                </button>
              </div>
            )}

            {/* Navigation Menu */}
            <nav className="sidebar-nav-new">
              <div className="nav-group-new">
                <div className="nav-group-label-new">Main</div>
                <div className="nav-items-new">
                {/* Emergency Button - Moved to top for quick access */}
                <button 
                  className="nav-item-new emergency-item-new"
                  onClick={handleEmergencyService}
                >
                  <div className="nav-item-icon-new emergency-icon-new">
                  <i data-feather="phone-call"></i>
                  </div>
                  {!sidebarCollapsed && (
                    <>
                      <span className="nav-item-text-new">Emergency</span>
                      <span className="emergency-badge-new">24/7</span>
                    </>
                  )}
                </button>

                <button 
                    className={`nav-item-new ${activeTab === 'overview' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('overview'); setMobileMenuOpen(false); }}
                >
                    <div className="nav-item-icon-new">
                  <i data-feather="home"></i>
                    </div>
                    {!sidebarCollapsed && (
                      <>
                        <span className="nav-item-text-new">{t('dashboard.overview', language)}</span>
                        {activeTab === 'overview' && <div className="nav-item-indicator-new"></div>}
                      </>
                    )}
                </button>

                <button 
                    className={`nav-item-new ${activeTab === 'appointments' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('appointments'); setMobileMenuOpen(false); }}
                >
                    <div className="nav-item-icon-new">
                  <i data-feather="calendar"></i>
                    </div>
                    {!sidebarCollapsed && (
                      <>
                        <span className="nav-item-text-new">{t('dashboard.appointments', language)}</span>
                        {activeTab === 'appointments' && <div className="nav-item-indicator-new"></div>}
                      </>
                    )}
                </button>

                <button 
                    className={`nav-item-new ${activeTab === 'health' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('health'); setMobileMenuOpen(false); }}
                >
                    <div className="nav-item-icon-new">
                  <i data-feather="activity"></i>
                    </div>
                    {!sidebarCollapsed && (
                      <>
                        <span className="nav-item-text-new">{t('dashboard.healthRecords', language)}</span>
                        {activeTab === 'health' && <div className="nav-item-indicator-new"></div>}
                      </>
                    )}
                </button>
                </div>
              </div>

              <div className="nav-group-new">
                <div className="nav-group-label-new">{t('dashboard.services', language)}</div>
                <div className="nav-items-new">
                <button 
                    className={`nav-item-new ${activeTab === 'marketplace' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('marketplace'); setMobileMenuOpen(false); }}
                >
                    <div className="nav-item-icon-new">
                  <i data-feather="shopping-bag"></i>
                    </div>
                    {!sidebarCollapsed && (
                      <>
                        <span className="nav-item-text-new">{t('dashboard.marketplace', language)}</span>
                        {activeTab === 'marketplace' && <div className="nav-item-indicator-new"></div>}
                      </>
                    )}
                </button>

                <button 
                    className={`nav-item-new ${activeTab === 'subscriptions' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('subscriptions'); setMobileMenuOpen(false); }}
                >
                    <div className="nav-item-icon-new">
                  <i data-feather="refresh-cw"></i>
                    </div>
                    {!sidebarCollapsed && (
                      <>
                        <span className="nav-item-text-new">{t('dashboard.subscriptions', language)}</span>
                        {activeTab === 'subscriptions' && <div className="nav-item-indicator-new"></div>}
                      </>
                    )}
                </button>

                <button 
                    className={`nav-item-new ${activeTab === 'billing' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('billing'); setMobileMenuOpen(false); }}
                >
                    <div className="nav-item-icon-new">
                  <i data-feather="credit-card"></i>
                    </div>
                    {!sidebarCollapsed && (
                      <>
                        <span className="nav-item-text-new">{t('dashboard.billing', language)}</span>
                        {activeTab === 'billing' && <div className="nav-item-indicator-new"></div>}
                      </>
                    )}
                </button>
                </div>
              </div>

              <div className="nav-group-new">
                <div className="nav-group-label-new">Support</div>
                <div className="nav-items-new">
                <button 
                    className={`nav-item-new ${activeTab === 'customer-care' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('customer-care'); setMobileMenuOpen(false); }}
                >
                    <div className="nav-item-icon-new">
                  <i data-feather="headphones"></i>
                    </div>
                    {!sidebarCollapsed && (
                      <>
                        <span className="nav-item-text-new">Customer Care</span>
                        {activeTab === 'customer-care' && <div className="nav-item-indicator-new"></div>}
                      </>
                    )}
                </button>
                </div>
              </div>

              {/* Bottom Actions */}
              {!sidebarCollapsed && (
                <div className="sidebar-footer-new">
                  {/* Row 1: Language Toggle and Dark Mode */}
                  <div className="footer-row">
                    <div className="sidebar-language-toggle">
                      <LanguageToggle />
                    </div>
                    <button 
                      className="footer-action-new"
                      onClick={toggleDarkMode}
                      title={isDarkMode ? t('dashboard.lightMode', language) : t('dashboard.darkMode', language)}
                    >
                      <i data-feather={isDarkMode ? "sun" : "moon"}></i>
                      <span>{isDarkMode ? t('dashboard.lightMode', language) : t('dashboard.darkMode', language)}</span>
                    </button>
                  </div>
                  {/* Row 2: Settings and Logout */}
                  <div className="footer-row">
                    <button 
                      className="footer-action-new"
                      onClick={() => { setShowProfileEdit(true); setMobileMenuOpen(false); }}
                    >
                      <i data-feather="settings"></i>
                      <span>{t('common.settings', language)}</span>
                    </button>
                    <button 
                      className="footer-action-new"
                      onClick={handleLogout}
                    >
                      <i data-feather="log-out"></i>
                      <span>{t('common.logout', language)}</span>
                    </button>
                  </div>
              </div>
              )}
              {/* Dark Mode Toggle for Collapsed Sidebar */}
              {sidebarCollapsed && (
                <div className="sidebar-footer-new">
                  <div className="sidebar-language-toggle-collapsed">
                    <LanguageToggle />
                  </div>
                  <button 
                    className="footer-action-new"
                    onClick={toggleDarkMode}
                    title={isDarkMode ? t('dashboard.switchToLightMode', language) : t('dashboard.switchToDarkMode', language)}
                  >
                    <i data-feather={isDarkMode ? "sun" : "moon"}></i>
                  </button>
              </div>
              )}
            </nav>
          </aside>

          {/* Main Content Area */}
          <main className="dashboard-main">
            {/* Content Area */}
            <div className="content-area">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="overview-content-modern">
                  {/* Banner Slideshow */}
                  <div className="banner-slideshow-container">
                    {/* #region agent log */}
                    {(() => {
                      const transformValue = bannerSlideIndex * (100 / 3);
                      return null;
                    })()}
                    {/* #endregion agent log */}
                    <div className="banner-slideshow-wrapper">
                      <div 
                        className="banner-slideshow-track"
                        style={{ transform: `translateX(-${bannerSlideIndex * (100 / 3)}%)` }}
                      >
                        {[
                          {
                            id: 1,
                            src: "https://res.cloudinary.com/dmmetrvda/image/upload/v1766359619/weheal/banners/banner-1_new.png",
                            alt: "Healthcare Banner 1",
                            fallback: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&h=400&fit=crop"
                          },
                          {
                            id: 2,
                            src: "https://res.cloudinary.com/dmmetrvda/image/upload/v1766359621/weheal/banners/banner-2_new.png",
                            alt: "Healthcare Banner 2",
                            fallback: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1200&h=400&fit=crop"
                          },
                          {
                            id: 3,
                            src: "https://res.cloudinary.com/dmmetrvda/image/upload/v1766359623/weheal/banners/banner-3_new.png",
                            alt: "Healthcare Banner 3",
                            fallback: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=1200&h=400&fit=crop"
                          }
                        ].map((banner) => (
                          <div key={banner.id} className="banner-slide">
                            <img 
                              src={bannerImageErrors[banner.id] ? banner.fallback : banner.src}
                              alt={banner.alt}
                            className="banner-image"
                              loading="eager"
                              onLoad={(e) => {
                                // #region agent log
                                // #endregion
                                // Clear error if image loads successfully
                                if (bannerImageErrors[banner.id]) {
                                  setBannerImageErrors(prev => {
                                    const newErrors = { ...prev };
                                    delete newErrors[banner.id];
                                    return newErrors;
                                  });
                                }
                            }}
                            onError={(e) => {
                                // #region agent log
                                // #endregion
                                console.error(`Failed to load banner image ${banner.id}:`, e.target.src);
                                // Mark this banner as failed and switch to fallback
                                if (!bannerImageErrors[banner.id]) {
                                  setBannerImageErrors(prev => ({
                                    ...prev,
                                    [banner.id]: true
                                  }));
                                }
                                // Try fallback image if we haven't already
                                if (e.target.src !== banner.fallback && !e.target.src.includes('unsplash.com')) {
                                  e.target.src = banner.fallback;
                                }
                            }}
                          />
                        </div>
                        ))}
                      </div>
                    </div>
                    {/* Banner Navigation Dots */}
                    <div className="banner-dots">
                      {[0, 1, 2].map((index) => (
                        <button
                          key={index}
                          className={`banner-dot ${bannerSlideIndex === index ? 'active' : ''}`}
                          onClick={() => {
                            setBannerSlideIndex(index);
                            if (bannerIntervalRef.current) {
                              clearInterval(bannerIntervalRef.current);
                            }
                            bannerIntervalRef.current = setInterval(() => {
                              setBannerSlideIndex((prevIndex) => (prevIndex + 1) % 3);
                            }, 5000);
                          }}
                          aria-label={`Go to slide ${index + 1}`}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Greeting Section */}
                  <div className="greeting-section-modern">
                    <h1 className="greeting-title-modern">{t('dashboard.hello', language)}, {user?.name?.split(' ')[0] || t('dashboard.there', language)}!</h1>
                    <p className="greeting-subtitle-modern">{t('dashboard.howAreYouFeeling', language)}</p>
                      </div>

                  {/* Information Cards - Your Doctor & Your Data */}
                  <div className="info-cards-row-modern">
                    {/* Your Doctor Card */}
                    <div className="info-card-modern doctor-card-modern">
                      <div className="info-card-header-modern">
                        <h3 className="info-card-title-modern">{t('dashboard.yourDoctor', language)}</h3>
                        <button className="btn-change-modern" onClick={() => setShowDoctorSearch(true)} title={t('dashboard.change', language)}>
                          <i data-feather="edit-2"></i>
                        </button>
                      </div>
                      <div className="info-card-content-modern">
                        {dashboardData.patientData?.upcomingAppointments?.[0] ? (() => {
                          const appointment = dashboardData.patientData.upcomingAppointments[0];
                          const isTeleConsult = appointment.type === 'tele-consult';
                          const isScheduled = appointment.status === 'scheduled' || appointment.status === 'confirmed';
                          
                          return (
                            <>
                              <div className="doctor-avatar-modern">
                                {appointment.doctorProfilePicture ? (
                                  <img 
                                    src={getImageUrl(appointment.doctorProfilePicture)} 
                                    alt="Doctor"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      if (e.target.nextElementSibling) {
                                        e.target.nextElementSibling.style.display = 'flex';
                                      }
                                    }}
                                  />
                                ) : null}
                                <i data-feather="user" style={{ display: appointment.doctorProfilePicture ? 'none' : 'flex' }}></i>
                      </div>
                              <div className="doctor-info-modern">
                                <h4 className="doctor-name-modern">{appointment.doctor}</h4>
                                <p className="doctor-specialization-modern">{appointment.specialization || t('dashboard.generalPractitioner', language)}</p>
                                {isTeleConsult && (
                                  <span className="appointment-type-badge-modern">
                                    <i data-feather="video"></i>
                                    {t('dashboard.videoConsultation', language)}
                                  </span>
                                )}
                              </div>
                              <div className="doctor-actions-modern">
                                {isTeleConsult && isScheduled ? (
                      <button
                                    className="btn-video-call-modern" 
                                    onClick={() => handleStartVideoCall(appointment._id)}
                                    title={t('dashboard.joinCall', language)}
                                  >
                                    <i data-feather="video"></i>
                                    <span>{t('dashboard.joinCall', language)}</span>
                      </button>
                                ) : (
                                  <>
                                    <button className="btn-action-modern" title={t('dashboard.message', language)}>
                                      <i data-feather="message-circle"></i>
                                    </button>
                                    <button className="btn-action-modern" title={t('dashboard.call', language)}>
                                      <i data-feather="phone"></i>
                                    </button>
                                  </>
                                )}
                      </div>
                            </>
                          );
                        })() : (
                          <div className="no-doctor-state-modern">
                            <i data-feather="user-x"></i>
                            <p>{t('dashboard.noAssignedDoctor', language)}</p>
                            <button className="btn-primary-modern" onClick={() => setShowDoctorSearch(true)}>
                              {t('dashboard.findDoctor', language)}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Your Data Card */}
                    <div className="info-card-modern data-card-modern">
                      <div className="info-card-header-modern">
                        <h3 className="info-card-title-modern">{t('dashboard.yourData', language)}</h3>
                        <button className="btn-change-modern" onClick={() => setShowProfileEdit(true)} title={t('dashboard.change', language)}>
                          <i data-feather="edit-2"></i>
                        </button>
                      </div>
                      <div className="info-card-content-modern">
                        <div className="data-list-modern">
                          <div className="data-row-modern">
                            <span className="data-label-modern">{t('dashboard.weight', language)}</span>
                            <span className="data-value-modern">{profileData.weight || t('dashboard.noData', language)}{profileData.weight ? ` ${t('dashboard.kg', language)}` : ''}</span>
                      </div>
                          <div className="data-row-modern">
                            <span className="data-label-modern">{t('dashboard.height', language)}</span>
                            <span className="data-value-modern">{profileData.height || t('dashboard.noData', language)}{profileData.height ? ` ${t('dashboard.m', language)}` : ''}</span>
                      </div>
                          <div className="data-row-modern">
                            <span className="data-label-modern">{t('dashboard.blood', language)}</span>
                            <span className="data-value-modern">{profileData.bloodGroup || t('dashboard.noData', language)}</span>
                    </div>
                  </div>
                    </div>
                        </div>
                          </div>

                  {/* Feature Cards Grid */}
                  <div className="feature-cards-section-modern">
                    <div className="feature-cards-grid-modern">
                      <div className="feature-card-modern" onClick={() => setActiveTab('health')}>
                        <div className="feature-icon-modern">
                          <i data-feather="file-text"></i>
                        </div>
                        <h4 className="feature-title-modern">{t('dashboard.diagnostic', language)}</h4>
                        <p className="feature-description-modern">{t('dashboard.listOfDiseases', language)}</p>
                        <button className="feature-add-btn-modern" onClick={(e) => { e.stopPropagation(); setActiveTab('health'); }}>
                          <i data-feather="plus"></i>
                        </button>
                      </div>
                      
                      <div className="feature-card-modern" onClick={() => setActiveTab('subscriptions')}>
                        <div className="feature-icon-modern">
                          <i data-feather="package"></i>
                        </div>
                        <h4 className="feature-title-modern">{t('dashboard.myDrugs', language)}</h4>
                        <p className="feature-description-modern">{t('dashboard.prescribedMedicine', language)}</p>
                        <button className="feature-add-btn-modern" onClick={(e) => { e.stopPropagation(); setActiveTab('subscriptions'); }}>
                          <i data-feather="plus"></i>
                        </button>
                  </div>

                      <div className="feature-card-modern" onClick={() => navigate('/lab-tests/my-reports')}>
                        <div className="feature-icon-modern">
                          <i data-feather="clipboard"></i>
                      </div>
                        <h4 className="feature-title-modern">{t('dashboard.tests', language)}</h4>
                        <p className="feature-description-modern">{t('dashboard.labTestResults', language)}</p>
                        <button className="feature-add-btn-modern" onClick={(e) => { e.stopPropagation(); navigate('/lab-tests/my-reports'); }}>
                          <i data-feather="plus"></i>
                                </button>
                            </div>

                      <div className="feature-card-modern" onClick={() => setShowDoctorSearch(true)}>
                        <div className="feature-icon-modern">
                          <i data-feather="search"></i>
                          </div>
                        <h4 className="feature-title-modern">Find Doctor</h4>
                        <p className="feature-description-modern">{t('dashboard.bookAppointment', language)}</p>
                        <button className="feature-add-btn-modern" onClick={(e) => { e.stopPropagation(); setShowDoctorSearch(true); }}>
                          <i data-feather="plus"></i>
                            </button>
                          </div>
                      </div>
                  </div>
                </div>
              )}

              {activeTab === 'customer-care' && (
                <div className="section customer-care-section">
                  <div className="customer-care-header">
                    <h2>Customer Care</h2>
                    <p>We're here to help. Reach out through any of the options below.</p>
                  </div>
                  <div className="customer-care-cards-container">
                    <div className="customer-care-two-column">
                      <div className="customer-care-card">
                        <div className="customer-care-card-content">
                          <h3>WhatsApp Chat</h3>
                          <p className="customer-care-card-subtitle">24/7 support via WhatsApp</p>
                          <a 
                            href="https://wa.me/+8801308538775?text=Hi%20I%20need%20help%20with%20WeHeal" 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="btn btn-primary customer-care-whatsapp-btn"
                          >
                            <span className="btn-whatsapp-icon">💬</span>
                            CHAT ON WHATSAPP
                          </a>
                        </div>
                      </div>
                      <div className="customer-care-card">
                        <div className="customer-care-card-content">
                          <h3>Email</h3>
                          <p className="customer-care-card-subtitle">We usually respond within a few hours</p>
                          <div className="customer-care-email-field">
                            <a href="mailto:wehealbd2025@gmail.com" className="customer-care-email-link">WEHEALBD2025@GMAIL.COM</a>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* AI Health Support Card */}
                    <div className="customer-care-card customer-care-ai-card">
                      <div className="customer-care-card-content">
                        <h3>🤖 AI Health Support Assistant</h3>
                        <p className="customer-care-card-subtitle">Try out our best-in-class AI health support assistant to guide you in every step</p>
                        <button 
                          onClick={() => setAiDrawerOpen(true)}
                          className="btn btn-primary customer-care-ai-btn"
                        >
                          <span className="btn-ai-icon">💬</span>
                          TRY AI ASSISTANT
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="customer-care-chat-section">
                    {!chatOpen ? (
                      <div className="customer-care-live-chat-wrapper">
                        <button className="btn btn-primary customer-care-live-chat-btn" onClick={startChat}>{t('dashboard.liveChat', language).toUpperCase()}</button>
                      </div>
                    ) : (
                      <div className="card">
                        <div className="card-body">
                          <div className="customer-care-chat-header">
                            <div>
                              <strong>{t('dashboard.liveChat', language)}</strong>
                              <div className="customer-care-chat-status">
                                {chatAssignedAgent ? `${t('dashboard.connectedTo', language)} ${chatAssignedAgent.name || t('dashboard.agent', language)}` : t('dashboard.waitingForAgent', language)}
                              </div>
                            </div>
                            <button className="btn btn-secondary btn-sm" onClick={endChat}>{t('dashboard.endChat', language)}</button>
                          </div>
                          <div className="customer-care-chat-messages">
                            {chatMessages.length === 0 ? (
                              <div className="no-messages">{t('dashboard.noMessagesYet', language)}</div>
                            ) : (
                              chatMessages.map((m, idx) => (
                                <div key={idx} className={`customer-care-chat-message ${m.from === 'agent' ? 'agent' : ''}`}>
                                  <div className={`customer-care-chat-bubble ${m.from === 'patient' ? 'patient' : 'agent'}`}>
                                    {m.type === 'image' ? (
                                      <div className="chat-image-container">
                                        <img src={m.imageUrl} alt={m.fileName || 'Image'} />
                                        <div className="chat-image-filename">{m.fileName}</div>
                                      </div>
                                    ) : (
                                      m.text
                                    )}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                          <div className="customer-care-chat-input-area">
                            <input 
                              value={chatInput} 
                              onChange={(e) => setChatInput(e.target.value)} 
                              placeholder={t('dashboard.typeYourMessage', language)} 
                              className="form-input customer-care-chat-input"
                            />
                            <button className="btn btn-primary btn-sm" onClick={sendChat}>{t('dashboard.send', language)}</button>
                            <button className="btn btn-danger btn-sm" onClick={endChat}>{t('dashboard.endChat', language)}</button>
                          </div>
                          
                          {/* Image Upload Section */}
                          <div className="customer-care-image-upload">
                            <div className="customer-care-image-upload-controls">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                style={{ display: 'none' }}
                                id="chat-image-upload"
                              />
                              <label htmlFor="chat-image-upload" className="btn btn-outline btn-sm">
                                📷 {t('dashboard.addImage', language)}
                              </label>
                              {chatImageUpload && (
                                <>
                                  <button className="btn btn-primary btn-sm" onClick={sendImage}>
                                    {t('dashboard.sendImage', language)}
                                  </button>
                                  <button className="btn btn-secondary btn-sm" onClick={removeImage}>
                                    {t('dashboard.remove', language)}
                                  </button>
                                </>
                              )}
                            </div>
                            
                            {/* Image Preview */}
                            {chatImagePreview && (
                              <div className="customer-care-image-preview">
                                <img src={chatImagePreview} alt="Preview" />
                                <div className="customer-care-image-preview-info">
                                  <div><strong>{chatImageUpload?.name}</strong></div>
                                  <div>{(chatImageUpload?.size / 1024 / 1024).toFixed(2)} MB</div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Appointments Tab */}
              {activeTab === 'appointments' && (
                <div className="appointments-content">
                  <div className="content-header">
                    <button 
                      className="btn btn-primary"
                      onClick={() => setShowDoctorSearch(true)}
                    >
                      <i data-feather="plus"></i>
                      {t('dashboard.bookNewAppointment', language)}
                    </button>
                  </div>

                  <div className="appointments-grid">
                    {dashboardData.patientData?.upcomingAppointments && 
                     dashboardData.patientData.upcomingAppointments.length > 0 ? (
                      dashboardData.patientData.upcomingAppointments.map((appointment, index) => (
                        <div key={appointment._id || index} className="appointment-card-new">
                          <div className="appointment-card-header">
                            <div className="doctor-profile">
                              <div className="doctor-avatar">
                                {appointment.doctorProfilePicture ? (
                                  <img 
                                    src={getImageUrl(appointment.doctorProfilePicture)} 
                                    alt={appointment.doctor}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      if (e.target.nextElementSibling) {
                                        e.target.nextElementSibling.style.display = 'flex';
                                      }
                                    }}
                                  />
                                ) : null}
                                <div className="doctor-avatar-placeholder" style={{ display: appointment.doctorProfilePicture ? 'none' : 'flex' }}>
                                  <i data-feather="user"></i>
                                </div>
                              </div>
                              <div className="doctor-info">
                                <h3>{appointment.doctor}</h3>
                                <p className="specialization">{appointment.specialization || t('dashboard.generalPractitioner', language)}</p>
                              </div>
                            </div>
                            <div className={`status-badge-new status-${appointment.status?.toLowerCase()}`}>
                              {appointment.status}
                            </div>
                          </div>

                          <div className="appointment-details-new">
                            <div className="detail-row">
                              <div className="detail-item">
                                <i data-feather="calendar"></i>
                                <div>
                                  <span className="label">{t('appointments.date', language)}</span>
                                  <span className="value">{new Date(appointment.date).toLocaleDateString('en-US', { 
                                    weekday: 'long', 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric' 
                                  })}</span>
                                </div>
                              </div>
                              <div className="detail-item">
                                <i data-feather="clock"></i>
                                <div>
                                  <span className="label">{t('appointments.time', language)}</span>
                                  <span className="value">{appointment.time || `${appointment.startTime} - ${appointment.endTime}`}</span>
                                </div>
                              </div>
                            </div>
                            <div className="detail-row">
                              <div className="detail-item">
                                <i data-feather={appointment.type === 'tele-consult' ? 'video' : 'map-pin'}></i>
                                <div>
                                  <span className="label">{t('dashboard.type', language)}</span>
                                  <span className="value">{appointment.type === 'tele-consult' ? t('dashboard.videoConsultation', language) : t('dashboard.inPersonVisit', language)}</span>
                                </div>
                              </div>
                              <div className="detail-item">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ fontSize: '20px', fontWeight: 'bold' }}>
                                  <text x="12" y="18" textAnchor="middle" fontSize="18" fill="currentColor" fontFamily="Arial, sans-serif">৳</text>
                                </svg>
                                <div>
                                  <span className="label">{t('dashboard.fee', language)}</span>
                                  <span className="value">
                                    {appointment.consultationFee ? `৳${Number(appointment.consultationFee).toLocaleString('en-BD')}` : 
                                     appointment.paymentAmount ? `৳${Number(appointment.paymentAmount).toLocaleString('en-BD')}` :
                                     appointment.payment?.amount ? `৳${Number(appointment.payment.amount).toLocaleString('en-BD')}` :
                                     appointment.fee ? `৳${Number(appointment.fee).toLocaleString('en-BD')}` :
                                     '৳N/A'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="appointment-actions-new">
                            {appointment.type === 'tele-consult' && appointment.status === 'scheduled' && (
                              <button 
                                className="btn btn-primary"
                                onClick={() => handleStartVideoCall(appointment._id)}
                              >
                                <i data-feather="video"></i>
                                {t('dashboard.joinVideoCall', language)}
                              </button>
                            )}
                            <button 
                              className="btn btn-outline"
                              onClick={() => handleCancelAppointment(appointment._id)}
                              disabled={cancellingAppointment}
                            >
                              <i data-feather="x"></i>
                              {cancellingAppointment ? t('dashboard.cancelling', language) : t('dashboard.cancelAppointment', language)}
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="empty-state-large">
                        <div className="empty-icon">
                          <i data-feather="calendar"></i>
                        </div>
                        <h3>{t('dashboard.noAppointmentsScheduled', language)}</h3>
                        <p>{t('dashboard.noAppointmentsMessage', language)}</p>
                        <button className="btn btn-primary" onClick={() => setShowDoctorSearch(true)}>
                          <i data-feather="search"></i>
                          Find a Doctor
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Health Records Tab */}
              {activeTab === 'health' && (
                <div className="health-content">
                  <div style={{ padding: '2rem' }}>
                    <ReportsSection />
                  </div>
                </div>
              )}



              {activeTab === 'marketplace' && (
                <div className="marketplace-content" style={{ padding: '2rem' }}>
                  <div className="marketplace-dashboard">
                    {/* Simplified Header */}
                    <div className="marketplace-header">
                      <div className="marketplace-header-content">
                        <div>
                      <h2>Healthcare Marketplace</h2>
                      <p>Shop for medications, lab tests, and wellness products</p>
                        </div>
                        <button
                          onClick={toggleDarkMode}
                          className="marketplace-dark-mode-toggle"
                          title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                          aria-label={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                        >
                          {isDarkMode ? (
                            <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {/* Primary Quick Actions */}
                    <div className="marketplace-quick-actions">
                      <h3>Browse Marketplace</h3>
                      <div className="quick-action-grid">
                        <button 
                          className="quick-action-card"
                          onClick={() => navigate('/marketplace')}
                        >
                          <div className="action-icon">🛍️</div>
                          <h3>Browse All</h3>
                          <p>View all products</p>
                        </button>
                        
                        <button 
                          className="quick-action-card"
                          onClick={() => navigate('/marketplace/category/medicine')}
                        >
                          <div className="action-icon">💊</div>
                          <h3>Medicines</h3>
                          <p>Browse medications</p>
                        </button>
                        
                        <button 
                          className="quick-action-card"
                          onClick={() => navigate('/lab-tests')}
                        >
                          <div className="action-icon">🧪</div>
                          <h3>Lab Tests</h3>
                          <p>Book diagnostic tests</p>
                        </button>
                        
                        <button 
                          className="quick-action-card"
                          onClick={() => navigate('/cart')}
                        >
                          <div className="action-icon">🛒</div>
                          <h3>My Cart</h3>
                          <p>View cart items</p>
                        </button>
                        
                        <button 
                          className="quick-action-card"
                          onClick={() => navigate('/lab-tests/my-tests')}
                        >
                          <div className="action-icon">📋</div>
                          <h3>My Lab Tests</h3>
                          <p>Track test orders</p>
                        </button>
                        
                        <button 
                          className="quick-action-card"
                          onClick={() => navigate('/orders')}
                        >
                          <div className="action-icon">📦</div>
                          <h3>My Orders</h3>
                          <p>View order history</p>
                        </button>
                      </div>
                    </div>
                    
                    {/* Marketplace Stats */}
                    <div>
                      <h3 className="marketplace-stats-title">Marketplace Information</h3>
                      <div className="marketplace-stats-grid">
                        <div className="marketplace-stat-card">
                          <div className="marketplace-stat-card-icon">💊</div>
                          <div className="marketplace-stat-card-value">500+</div>
                          <div className="marketplace-stat-card-label">Medicines</div>
                        </div>
                        
                        <div className="marketplace-stat-card">
                          <div className="marketplace-stat-card-icon">🧪</div>
                          <div className="marketplace-stat-card-value">50+</div>
                          <div className="marketplace-stat-card-label">Lab Tests</div>
                        </div>
                        
                        <div className="marketplace-stat-card">
                          <div className="marketplace-stat-card-icon">🚚</div>
                          <div className="marketplace-stat-card-value">Free</div>
                          <div className="marketplace-stat-card-label">Delivery</div>
                        </div>
                        
                        <div className="marketplace-stat-card">
                          <div className="marketplace-stat-card-icon">🔒</div>
                          <div className="marketplace-stat-card-value">100%</div>
                          <div className="marketplace-stat-card-label">Authentic</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Enhanced Info Cards */}
                    <div className="marketplace-info">
                      <h3>Why Choose Our Marketplace?</h3>
                      <div className="marketplace-info-grid">
                        <div className="marketplace-info-card">
                          <div className="marketplace-info-card-icon">🚚</div>
                          <h4>Free Delivery</h4>
                          <p>Free delivery on all orders with fast and reliable shipping to your doorstep</p>
                        </div>
                        
                        <div className="marketplace-info-card">
                          <div className="marketplace-info-card-icon">💳</div>
                          <h4>Cash on Delivery</h4>
                          <p>Pay when you receive your order - convenient and secure payment option</p>
                        </div>
                        
                        <div className="marketplace-info-card">
                          <div className="marketplace-info-card-icon">🔒</div>
                          <h4>Secure & Authentic</h4>
                          <p>All products are verified, authentic, and sourced from trusted suppliers</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'billing' && (
                <div className="billing-content">
                  <div className="left-col">
                    {/* Billing Summary */}
                    <div className="card">
                      <div className="card-header">
                        <h3>Billing Summary</h3>
                      </div>
                      <div className="card-body">
                        <BillingSection setActiveTab={setActiveTab} />
                      </div>
                    </div>
                    
                    {/* Full Payment History */}
                    <div className="card">
                      <div className="card-header">
                        <h2>Complete Payment History</h2>
                      </div>
                      <div className="card-body">
                        <PaymentHistory />
                      </div>
                    </div>
                  </div>
                  <div className="right-col">
                    <LoyaltyCard />
                    <InsuranceCard />
                    <ShippingAddressCard />
                    <BankDetailsCard />
                    <CardDetailsCard />
                    <MobileBankingCard />
                  </div>
                </div>
              )}

              {activeTab === 'subscriptions' && (
                <div className="subscription-content">
                  <SubscriptionManagement />
                </div>
              )}

              {/* Prescriptions Tab */}
              {activeTab === 'prescriptions' && (
                <div className="prescriptions-content">
                  <PrescriptionList userRole="patient" />
                </div>
              )}
            </div>
          </main>
        </div>
      )}

      {/* Profile Edit Modal - Redesigned with Material-UI */}
      <Dialog
        open={showProfileEdit}
        onClose={() => setShowProfileEdit(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF',
            border: isDarkMode ? '1px solid rgba(59, 130, 246, 0.2)' : 'none',
            borderRadius: { xs: 2, sm: 3 },
            maxHeight: '90vh',
            overflow: 'auto',
            width: { xs: '95%', sm: 'auto' },
            maxWidth: { xs: '95%', sm: '600px' },
            m: { xs: 1, sm: 2 }
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: isDarkMode 
              ? 'rgba(30, 64, 175, 0.2)' 
              : 'rgba(30, 64, 175, 0.05)',
            borderBottom: isDarkMode 
              ? '1px solid rgba(59, 130, 246, 0.2)' 
              : '1px solid rgba(30, 64, 175, 0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2,
            px: 3
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              background: isDarkMode
                ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
                : 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            Edit Profile
          </Typography>
          <IconButton
            onClick={() => setShowProfileEdit(false)}
            sx={{
              color: isDarkMode ? '#BFDBFE' : '#64748B',
              '&:hover': {
                bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF', p: { xs: 2, sm: 3 } }}>
          <form id="profile-edit-form" onSubmit={handleProfileUpdate}>
            {/* Profile Picture Section */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 4,
                pb: 3,
                borderBottom: isDarkMode 
                  ? '1px solid rgba(59, 130, 246, 0.2)' 
                  : '1px solid rgba(30, 64, 175, 0.1)'
              }}
            >
              <Avatar
                src={profilePicture}
                sx={{
                  width: { xs: 100, sm: 120 },
                  height: { xs: 100, sm: 120 },
                  mb: 2,
                  border: isDarkMode 
                    ? '4px solid rgba(59, 130, 246, 0.3)' 
                    : '4px solid rgba(30, 64, 175, 0.2)',
                  bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(30, 64, 175, 0.1)',
                  fontSize: { xs: 40, sm: 50 }
                }}
              >
                {!profilePicture && (
                  <Typography
                    sx={{
                      fontSize: { xs: 40, sm: 50 },
                      color: isDarkMode ? '#60A5FA' : '#1E40AF',
                      fontWeight: 700
                    }}
                  >
                    {profileData.name?.charAt(0)?.toUpperCase() || 'U'}
                  </Typography>
                )}
              </Avatar>
              <Button
                variant="outlined"
                startIcon={<CameraAltIcon />}
                  onClick={() => fileInputRef.current?.click()}
                sx={{
                  borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
                  color: isDarkMode ? '#60A5FA' : '#1E40AF',
                  '&:hover': {
                    borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                    bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)'
                  }
                }}
              >
                  Change Picture
              </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePictureUpload}
                  style={{ display: 'none' }}
                />
            </Box>

            {/* Personal Information */}
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                color: isDarkMode ? '#E0F2FE' : '#1E293B',
                fontWeight: 600
              }}
            >
              Personal Information
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                      value={profileData.name}
                      onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Birth Date"
                  type="date"
                  value={profileData.birthdate}
                  onChange={(e) => {
                    const newBirthdate = e.target.value;
                    setProfileData({...profileData, birthdate: newBirthdate});
                    // Calculate age from birthdate
                    if (newBirthdate) {
                      const today = new Date();
                      const birthDate = new Date(newBirthdate);
                      let calculatedAge = today.getFullYear() - birthDate.getFullYear();
                      const monthDiff = today.getMonth() - birthDate.getMonth();
                      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                        calculatedAge--;
                      }
                      setProfileData(prev => ({...prev, birthdate: newBirthdate, age: calculatedAge.toString()}));
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Age (Auto-calculated)"
                  type="number"
                  value={profileData.age}
                  onChange={(e) => setProfileData({...profileData, age: e.target.value})}
                  disabled={!!profileData.birthdate}
                  helperText={profileData.birthdate ? "Age is calculated from birth date" : "Enter age manually or set birth date"}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Weight (kg)"
                      type="number"
                      value={profileData.weight}
                      onChange={(e) => setProfileData({...profileData, weight: e.target.value})}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Height (cm)"
                      type="number"
                      value={profileData.height}
                      onChange={(e) => setProfileData({...profileData, height: e.target.value})}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>
                    Blood Group
                  </InputLabel>
                  <Select
                      value={profileData.bloodGroup}
                      onChange={(e) => setProfileData({...profileData, bloodGroup: e.target.value})}
                    label="Blood Group"
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF',
                          border: isDarkMode ? '1px solid rgba(59, 130, 246, 0.2)' : 'none',
                          '& .MuiMenuItem-root': {
                            color: isDarkMode ? '#E0F2FE' : '#1E293B',
                            '&:hover': {
                              bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)'
                            },
                            '&.Mui-selected': {
                              bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(30, 64, 175, 0.1)',
                              '&:hover': {
                                bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.15)'
                              }
                            }
                          }
                        }
                      }
                    }}
                  >
                    <MenuItem value="">Select Blood Group</MenuItem>
                    <MenuItem value="A+">A+</MenuItem>
                    <MenuItem value="A-">A-</MenuItem>
                    <MenuItem value="B+">B+</MenuItem>
                    <MenuItem value="B-">B-</MenuItem>
                    <MenuItem value="AB+">AB+</MenuItem>
                    <MenuItem value="AB-">AB-</MenuItem>
                    <MenuItem value="O+">O+</MenuItem>
                    <MenuItem value="O-">O-</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  multiline
                  rows={3}
                    value={profileData.address}
                    onChange={(e) => setProfileData({...profileData, address: e.target.value})}
                />
              </Grid>
            </Grid>

            <Divider 
              sx={{ 
                my: 3,
                borderColor: isDarkMode 
                  ? 'rgba(59, 130, 246, 0.2)' 
                  : 'rgba(30, 64, 175, 0.1)'
              }} 
            />

            {/* Emergency Contact */}
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                color: isDarkMode ? '#E0F2FE' : '#1E293B',
                fontWeight: 600
              }}
            >
              Emergency Contact
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Name"
                        value={profileData.emergencyContact.name}
                        onChange={(e) => setProfileData({
                          ...profileData, 
                          emergencyContact: {...profileData.emergencyContact, name: e.target.value}
                        })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Phone"
                        type="tel"
                        value={profileData.emergencyContact.phone}
                        onChange={(e) => setProfileData({
                          ...profileData, 
                          emergencyContact: {...profileData.emergencyContact, phone: e.target.value}
                        })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Relationship"
                        value={profileData.emergencyContact.relationship}
                        onChange={(e) => setProfileData({
                          ...profileData, 
                          emergencyContact: {...profileData.emergencyContact, relationship: e.target.value}
                        })}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>

        <DialogActions
          sx={{
            p: { xs: 2, sm: 3 },
            bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF',
            borderTop: isDarkMode 
              ? '1px solid rgba(59, 130, 246, 0.2)' 
              : '1px solid rgba(30, 64, 175, 0.1)',
            gap: 1,
            flexWrap: 'wrap',
            flexDirection: { xs: 'column', sm: 'row' },
            '& > button': {
              width: { xs: '100%', sm: 'auto' }
            }
          }}
        >
          <Button
            onClick={() => setShowProfileEdit(false)}
            sx={{
              color: isDarkMode ? '#BFDBFE' : '#64748B',
              '&:hover': {
                bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)'
              }
            }}
          >
                    Cancel
          </Button>
          <Button
            onClick={() => {
              setShowProfileEdit(false);
              setShowPasswordChange(true);
            }}
            startIcon={<LockIcon />}
            sx={{
              borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
              color: isDarkMode ? '#60A5FA' : '#1E40AF',
              '&:hover': {
                borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)'
              }
            }}
            variant="outlined"
          >
                    Change Password
          </Button>
          <Button
            type="submit"
            form="profile-edit-form"
            startIcon={<SaveIcon />}
            variant="contained"
            sx={{
              bgcolor: isDarkMode
                ? 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)'
                : 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
              color: '#FFFFFF',
              '&:hover': {
                bgcolor: isDarkMode
                  ? 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)'
                  : 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)',
              }
            }}
          >
                    Update Profile
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Change Modal - Redesigned with Material-UI */}
      <Dialog
        open={showPasswordChange}
        onClose={() => setShowPasswordChange(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF',
            border: isDarkMode ? '1px solid rgba(59, 130, 246, 0.2)' : 'none',
            borderRadius: 3
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: isDarkMode 
              ? 'rgba(30, 64, 175, 0.2)' 
              : 'rgba(30, 64, 175, 0.05)',
            borderBottom: isDarkMode 
              ? '1px solid rgba(59, 130, 246, 0.2)' 
              : '1px solid rgba(30, 64, 175, 0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2,
            px: 3
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              background: isDarkMode
                ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
                : 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            Change Password
          </Typography>
          <IconButton
            onClick={() => setShowPasswordChange(false)}
            sx={{
              color: isDarkMode ? '#BFDBFE' : '#64748B',
              '&:hover': {
                bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF', p: { xs: 2, sm: 3 } }}>
          <form id="password-change-form" onSubmit={handlePasswordChange}>
            <TextField
              fullWidth
              label="Current Password"
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                    required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="New Password"
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                    required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Confirm New Password"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                    required
            />
          </form>
        </DialogContent>

        <DialogActions
          sx={{
            p: { xs: 2, sm: 3 },
            bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF',
            borderTop: isDarkMode 
              ? '1px solid rgba(59, 130, 246, 0.2)' 
              : '1px solid rgba(30, 64, 175, 0.1)',
            gap: 1,
            flexDirection: { xs: 'column', sm: 'row' },
            '& > button': {
              width: { xs: '100%', sm: 'auto' }
            }
          }}
        >
          <Button
            onClick={() => setShowPasswordChange(false)}
            sx={{
              color: isDarkMode ? '#BFDBFE' : '#64748B',
              '&:hover': {
                bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)'
              }
            }}
          >
                    Cancel
          </Button>
          <Button
            type="submit"
            form="password-change-form"
            startIcon={<SaveIcon />}
            variant="contained"
            sx={{
              bgcolor: isDarkMode
                ? 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)'
                : 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
              color: '#FFFFFF',
              '&:hover': {
                bgcolor: isDarkMode
                  ? 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)'
                  : 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)',
              }
            }}
          >
                    Change Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Health Support Button - Only show when dashboard is loaded */}
      {!loading && !error && dashboardData && (
        <Suspense fallback={null}>
          <AIHealthSupportButton activeTab={activeTab} isDarkMode={isDarkMode} />
          <AIChatDrawer 
            open={aiDrawerOpen} 
            onClose={() => setAiDrawerOpen(false)} 
            activeTab={activeTab}
            isDarkMode={isDarkMode}
          />
        </Suspense>
      )}
    </div>
    </ThemeProvider>
  );
};

const PaymentHistory = () => {
  const { user } = useOutletContext();
  const [allPayments, setAllPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAllPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('=== SIMPLIFIED PAYMENT FETCHING ===');
      console.log('User ID:', user?._id);
      
      // Simple approach: fetch all payments from a single endpoint
      const response = await api.get('/payments/all-history');
      console.log('All payments response:', response);
      
      if (response.data.success) {
        const payments = response.data.data || [];
        console.log('Payments received:', payments.length);
        setAllPayments(payments);
      } else {
        console.error('Failed to fetch payments:', response.data.message);
        setError('Failed to fetch payments: ' + response.data.message);
      }
      
    } catch (err) {
      console.error('Error fetching payments:', err);
      setError('Failed to load payments: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?._id) {
      fetchAllPayments();
    }
  }, [user?._id]);

  const refreshPayments = () => {
    fetchAllPayments();
  };

  if (loading) return (
    <div className="payment-history-loading">
      <div className="spinner"></div>
      <Typography>Loading payment history...</Typography>
    </div>
  );

  if (error) return (
    <Box sx={{ p: 3 }}>
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
      <Button onClick={refreshPayments} variant="outlined">
        Try Again
      </Button>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: 'var(--text-primary)' }}>
          Payment History
        </Typography>
        <Button 
          onClick={refreshPayments} 
          variant="outlined" 
          startIcon={<RefreshIcon />}
        >
          Refresh
        </Button>
      </Box>

      {allPayments.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            No payments found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your payment history will appear here once you make payments.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {allPayments.map((payment, index) => (
            <Paper key={payment._id || index} sx={{ p: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {payment.paymentType === 'marketplace' ? 'Marketplace Purchase' : 'Consultation'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Date: {new Date(payment.createdAt || payment.date).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Amount: ৳{Number(payment.amount || 0).toLocaleString('en-BD')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Method: {payment.paymentMethod}
                  </Typography>
                </Box>
                <Chip 
                  label={payment.status || 'Completed'} 
                  color={payment.status === 'completed' ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              
              {payment.orderId && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Order ID: {payment.orderId}
                </Typography>
              )}
              
              {payment.transactionId && (
                <Typography variant="body2" color="text.secondary">
                  Transaction ID: {payment.transactionId}
                </Typography>
              )}
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
};

const BillingSection = ({ setActiveTab }) => {
  const { user } = useOutletContext();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/payments/all-history');
      
      if (response.data.success) {
        setPayments(response.data.data || []);
      } else {
        setError('Failed to fetch billing data');
      }
    } catch (err) {
      setError('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?._id) {
      fetchBillingData();
    }
  }, [user?._id]);

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading billing information...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={fetchBillingData} variant="outlined" size="small">
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        Billing & Payment History
      </Typography>
      
      {payments.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography color="text.secondary">
            No payment history available
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {payments.slice(0, 5).map((payment, index) => (
            <Paper key={payment._id || index} sx={{ p: 2, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {payment.paymentType === 'marketplace' ? '🛒 Purchase' : '👨‍⚕️ Consultation'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(payment.createdAt || payment.date).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'success.main' }}>
                    ৳{Number(payment.amount || 0).toLocaleString('en-BD')}
                  </Typography>
                  <Chip 
                    label={payment.status || 'Paid'} 
                    size="small" 
                    color={payment.status === 'completed' ? 'success' : 'default'}
                  />
                </Box>
              </Box>
            </Paper>
          ))}
          
          {payments.length > 5 && (
            <Box sx={{ textAlign: 'center', pt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing 5 of {payments.length} payments
              </Typography>
              <Button 
                onClick={() => setActiveTab('billing')} 
                variant="text" 
                size="small"
                sx={{ mt: 1 }}
              >
                View All Payments
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PatientDashboard;

// Right column cards
const LoyaltyCard = () => {
  const [points, setPoints] = useState(0);
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/patient-profile/profile');
        
        if (res.data?.data?.loyaltyPoints != null) {
          setPoints(res.data.data.loyaltyPoints);
        }
      } catch (err) {
        console.error('Error fetching loyalty points for dashboard:', err);
      }
    })();
  }, []);
  return (
    <div className="card">
      <div className="card-header"><h3>Loyalty Points</h3></div>
      <div className="card-body"><div className="loyalty-points-display">{points} pts</div></div>
    </div>
  );
};

const InsuranceCard = () => <ProfileSectionCard 
  title="Health Insurance" 
  fieldPath="billing.insurance" 
  fields={[
    { key: 'provider', label: 'Provider', placeholder: 'Enter Insurance Provider' },
    { key: 'policyNumber', label: 'Policy No.', placeholder: 'Enter Policy Number' },
    { key: 'coverage', label: 'Coverage', placeholder: 'Enter Coverage Amount' },
    { key: 'validTill', label: 'Valid Till', placeholder: 'YYYY-MM-DD', type: 'date' }
  ]} 
/>;
const ShippingAddressCard = () => <ProfileSectionCard title="Shipping Address" fieldPath="billing.shippingAddress" fields={[{ key: 'street', label: 'Street' }, { key: 'city', label: 'City' }, { key: 'state', label: 'State' }, { key: 'zipCode', label: 'ZIP' }, { key: 'country', label: 'Country' }]} />;
const BankDetailsCard = () => <ListSectionCard 
  title="Bank Accounts" 
  listFieldPath="paymentMethods.bankAccounts" 
  itemFields={[
    { key: 'bankName', label: 'Bank Name', placeholder: 'Enter Bank Name' },
    { key: 'accountNumber', label: 'Account Number', placeholder: 'Enter Account Number' },
    { key: 'accountHolder', label: 'Account Holder Name', placeholder: 'Enter Account Holder Name' },
    { key: 'branchName', label: 'Branch Name', placeholder: 'Enter Branch Name' }
  ]} 
  addTemplate={{ bankName: '', accountNumber: '', accountHolder: '', branchName: '' }} 
/>;
const CardDetailsCard = () => <ListSectionCard 
  title="Saved Cards" 
  listFieldPath="paymentMethods.cards" 
  itemFields={[
    { key: 'cardHolder', label: "Cardholder's Name", placeholder: "Enter Cardholder's Name" },
    { key: 'cardNumber', label: 'Card Number', placeholder: 'Enter Card Number' },
    { key: 'expiryDate', label: 'Expiry', placeholder: 'MM/YY' },
    { key: 'cvc', label: 'CVV', placeholder: 'Enter CVV' }
  ]} 
  addTemplate={{ cardHolder: '', cardNumber: '', expiryDate: '', cvc: '' }} 
/>;
const MobileBankingCard = () => <ListSectionCard 
  title="Mobile Wallets" 
  listFieldPath="paymentMethods.mobileWallets" 
  itemFields={[
    { key: 'provider', label: 'Provider', placeholder: 'Select Provider', type: 'select', options: ['bKash', 'Nagad', 'Upay'] },
    { key: 'mobileNumber', label: 'Mobile Number', placeholder: '01XXXXXXXXX' }
  ]} 
  addTemplate={{ provider: '', mobileNumber: '' }} 
/>;

// Generic single-object editor
const ProfileSectionCard = ({ title, fieldPath, fields }) => {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    const res = await api.get('/patient-profile/profile');
    const obj = res.data?.data || {};
    const value = fieldPath.split('.').reduce((acc, key) => (acc && acc[key] != null ? acc[key] : null), obj);
    setData(value);
    setForm(value || fields.reduce((acc, f) => ({ ...acc, [f.key]: '' }), {}));
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = {};
    const [root, sub] = fieldPath.split('.');
    payload[root] = { [sub]: form };
    await api.put('/patient-profile/profile', payload);
    setEditing(false);
    await load();
  };

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <h3>{title}</h3>
        <button className="btn btn-sm" onClick={() => setEditing(!editing)}>{editing ? 'Cancel' : (data ? 'Edit' : 'Add')}</button>
      </div>
      <div className="card-body">
        {data && !editing ? (
          <div>
            {fields.map(f => (
              <div key={f.key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                <span style={{ color: '#6B6B6B' }}>{f.label}</span>
                <span>{data[f.key] || '-'}</span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {fields.map(f => (
              <div key={f.key}>
                <label style={{ fontSize: '0.85rem', color: '#1E40AF', fontWeight: 600 }}>{f.label}</label>
                <input 
                  value={form[f.key] || ''} 
                  onChange={(e) => {
                    let value = e.target.value;
                    // Format card number
                    if (f.key === 'cardNumber') {
                      value = value.replace(/\D/g, '').replace(/(\d{4})/g, '$1 ').trim();
                    }
                    // Format expiry date
                    if (f.key === 'expiryDate') {
                      value = value.replace(/\D/g, '').replace(/(\d{2})(\d{2})/, '$1/$2');
                    }
                    // Limit CVC to 3 digits
                    if (f.key === 'cvc') {
                      value = value.replace(/\D/g, '').slice(0, 3);
                    }
                    // Limit mobile number to 11 digits
                    if (f.key === 'mobileNumber') {
                      value = value.replace(/\D/g, '').slice(0, 11);
                    }
                    setForm(prev => ({ ...prev, [f.key]: value }));
                  }}
                  placeholder={f.placeholder || `Enter ${f.label}`}
                  type={f.type === 'password' ? 'password' : f.type === 'number' ? 'text' : 'text'}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid transparent', borderRadius: 6, backgroundColor: '#F3F4F6', color: '#1E293B' }} 
                />
              </div>
            ))}
            <button className="btn btn-primary" onClick={save}>Save</button>
          </div>
        )}
      </div>
    </div>
  );
};

// Generic list editor
const ListSectionCard = ({ title, listFieldPath, itemFields, addTemplate }) => {
  const [list, setList] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(addTemplate);

  const load = async () => {
    const res = await api.get('/patient-profile/profile');
    const obj = res.data?.data || {};
    const value = listFieldPath.split('.').reduce((acc, key) => (acc && acc[key] != null ? acc[key] : null), obj);
    setList(Array.isArray(value) ? value : []);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    const [root, sub, leaf] = listFieldPath.split('.');
    
    // Process form data based on type
    let processedForm = { ...form };
    
    // For cards: extract brand, last4, don't save full cardNumber or cvc
    if (leaf === 'cards') {
      const cardNumber = form.cardNumber?.replace(/\s/g, '') || '';
      if (cardNumber.length >= 4) {
        processedForm.last4 = cardNumber.slice(-4);
        // Detect card brand from first digit
        const firstDigit = cardNumber[0];
        if (firstDigit === '4') processedForm.brand = 'Visa';
        else if (firstDigit === '5') processedForm.brand = 'Mastercard';
        else if (firstDigit === '3') processedForm.brand = 'American Express';
        else processedForm.brand = 'Card';
      }
      // Don't save cardNumber or cvc for security
      delete processedForm.cardNumber;
      delete processedForm.cvc;
    }
    
    // For bank accounts: mask account number
    if (leaf === 'bankAccounts') {
      const accountNumber = form.accountNumber || '';
      if (accountNumber.length >= 4) {
        processedForm.accountNumberMasked = '****' + accountNumber.slice(-4);
      }
      // Keep accountNumber for backend processing but also save masked version
    }
    
    const payload = { [root]: { [sub]: { [leaf]: [...list, processedForm] } } };
    await api.put('/patient-profile/profile', payload);
    setAdding(false);
    setForm(addTemplate);
    await load();
  };

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <h3>{title}</h3>
        <button className="btn btn-sm" onClick={() => setAdding(!adding)}>{adding ? 'Cancel' : 'Add'}</button>
      </div>
      <div className="card-body">
        {list.length === 0 && !adding && <div style={{ color: '#6B6B6B' }}>No {title.toLowerCase()} added yet.</div>}
        {list.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {list.map((item, idx) => (
              <div key={idx} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.5rem' }}>
                {itemFields.map(f => {
                  // For cards, show brand and last4 instead of full card number
                  if (f.key === 'cardNumber' && item.last4) {
                    return (
                      <div key={f.key} style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#6B6B6B' }}>Card</span>
                        <span>{item.brand || 'Card'} •••• {item.last4}</span>
                      </div>
                    );
                  }
                  // For bank accounts, show masked account number
                  if (f.key === 'accountNumber' && item.accountNumberMasked) {
                    return (
                      <div key={f.key} style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#6B6B6B' }}>{f.label}</span>
                        <span>{item.accountNumberMasked}</span>
                      </div>
                    );
                  }
                  // Skip displaying sensitive fields
                  if (f.key === 'cvc' || (f.key === 'cardNumber' && !item.last4)) {
                    return null;
                  }
                  return (
                    <div key={f.key} style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6B6B6B' }}>{f.label}</span>
                      <span>{item[f.key] || '-'}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
        {adding && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {itemFields.map(f => (
              <div key={f.key}>
                <label style={{ fontSize: '0.85rem', color: '#1E40AF', fontWeight: 600 }}>{f.label}</label>
                {f.type === 'select' && f.options ? (
                  <select
                    value={form[f.key] || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid transparent', borderRadius: 6, backgroundColor: '#F3F4F6', color: '#1E293B' }}
                  >
                    <option value="">{f.placeholder || `Select ${f.label}`}</option>
                    {f.options.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <input 
                    value={form[f.key] || ''} 
                    onChange={(e) => {
                      let value = e.target.value;
                      // Format card number
                      if (f.key === 'cardNumber') {
                        value = value.replace(/\D/g, '').replace(/(\d{4})/g, '$1 ').trim();
                      }
                      // Format expiry date
                      if (f.key === 'expiryDate') {
                        value = value.replace(/\D/g, '').replace(/(\d{2})(\d{2})/, '$1/$2');
                      }
                      // Limit CVC to 3 digits
                      if (f.key === 'cvc') {
                        value = value.replace(/\D/g, '').slice(0, 3);
                      }
                      // Limit mobile number to 11 digits
                      if (f.key === 'mobileNumber') {
                        value = value.replace(/\D/g, '').slice(0, 11);
                      }
                      setForm(prev => ({ ...prev, [f.key]: value }));
                    }}
                    placeholder={f.placeholder || `Enter ${f.label}`}
                    type={f.type === 'password' ? 'password' : f.type === 'number' ? 'text' : 'text'}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid transparent', borderRadius: 6, backgroundColor: '#F3F4F6', color: '#1E293B' }} 
                  />
                )}
              </div>
            ))}
            <button className="btn btn-primary" onClick={save}>Save</button>
          </div>
        )}
      </div>
    </div>
  );
};



